import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Logger } from '@saviynt/common';
import { Box, ButtonIcon, Icon, Tooltip } from '@saviynt/design-system';
import cx from 'classnames';

import { testIds } from '../../constants';

import './PromptInput.css';

interface PromptModuleProps {
  placeholder: string;
  isMultiline?: boolean;
  multiInputMaxHeight?: number;
  isProcessing?: boolean;
  isDisabled?: boolean;
  onSubmit: (text: string) => void;
  onAddFiles?: () => void;
  onStop?: () => void;
  onAudio?: (audio: string) => void;
  inputFieldId?: string;
  dataTestId?: string;
  className?: string;
}

/**
 * PromptInput
 */
function PromptInput({
  placeholder,
  isMultiline,
  multiInputMaxHeight,
  isProcessing,
  isDisabled,
  onSubmit,
  onAddFiles,
  onStop,
  onAudio,
  inputFieldId,
  dataTestId,
  className,
}: PromptModuleProps) {
  const [value, setValue] = useState('');
  const [isRecording, setIsRecording] = useState(false);

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);

  const isValidValue = useMemo(() => value.trim().length > 0, [value]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLElement>) => {
      if (e.key === 'Enter' && !e.shiftKey && isValidValue && !isProcessing) {
        onSubmit(value.trim());
        setValue('');
      }
    },
    [isProcessing, isValidValue, onSubmit, value]
  );

  // Convert Blob → Base64
  const blobToBase64 = (blob: Blob): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.readAsDataURL(blob);

      reader.onloadend = () => {
        resolve(reader.result as string);
      };

      reader.onerror = reject;
    });

  // Handle mic isRecording
  const handleMicClick = useCallback(async () => {
    if (!isRecording) {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: true,
        });
        const mediaRecorder = new MediaRecorder(stream);

        mediaRecorderRef.current = mediaRecorder;
        audioChunksRef.current = [];

        mediaRecorder.ondataavailable = (event) => {
          if (event.data.size > 0) {
            audioChunksRef.current.push(event.data);
          }
        };

        mediaRecorder.onstop = async () => {
          const audioBlob = new Blob(audioChunksRef.current, {
            type: 'audio/webm',
          });
          const base64Audio = await blobToBase64(audioBlob);

          onAudio?.(base64Audio);
        };

        mediaRecorder.start();
        setIsRecording(true);
      } catch (err) {
        Logger.log('Microphone access denied:', err);
      }
    } else {
      mediaRecorderRef.current?.stop();
      setIsRecording(false);
    }
  }, [isRecording, onAudio]);

  useEffect(() => {
    const textarea = textareaRef.current;

    if (textarea) {
      const computedStyle = window.getComputedStyle(textarea);
      const inputMaxHeight = parseInt(computedStyle.maxHeight, 10);

      textarea.style.height = 'auto';
      textarea.style.height = `${Math.min(
        textarea.scrollHeight,
        inputMaxHeight
      )}px`;

      if (textarea.scrollHeight > inputMaxHeight) {
        textarea.style.overflowY = 'scroll';
      } else {
        textarea.style.overflowY = 'hidden';
      }
    }
  }, [value]);

  const isNumber = (height: number | string) =>
    !Number.isNaN(Number(height)) && String(height).trim() !== '';

  const getInputBox = useCallback(() => {
    if (isMultiline) {
      return (
        <textarea
          id={inputFieldId}
          ref={textareaRef}
          placeholder={placeholder}
          disabled={isDisabled}
          autoComplete='off'
          value={value}
          onChange={(e) => {
            setValue(e.target.value);
          }}
          className='PromptInput-inputField'
          onKeyDown={handleKeyDown}
          rows={1}
          style={{
            maxHeight: `${
              multiInputMaxHeight && isNumber(multiInputMaxHeight)
                ? multiInputMaxHeight
                : 8
            }rem`,
          }}
        />
      );
    }

    return (
      <input
        id={inputFieldId}
        type='text'
        autoComplete='off'
        placeholder={placeholder}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        className='PromptInput-inputField'
        onKeyDown={handleKeyDown}
      />
    );
  }, [
    handleKeyDown,
    inputFieldId,
    isDisabled,
    isMultiline,
    multiInputMaxHeight,
    placeholder,
    value,
  ]);

  return (
    <Box
      tag='div'
      className={cx(
        'PromptInput',
        isDisabled && 'PromptInput--isDisabled',
        className
      )}
      dataTestId={dataTestId}>
      <Tooltip
        trigger={
          <ButtonIcon
            kind='saviGhost'
            ariaLabel='Add files'
            icon={<Icon kind='Add' size='medium' />}
            isDisabled={!onAddFiles || isDisabled}
            onClick={onAddFiles}
            dataTestId={testIds.GEN_PROMPT_INPUT_ADD_FILES_BTN}
          />
        }
        text='Add Files'
        placement='bottom'
      />
      <Box tag='div' className='PromptInput-wrapper'>
        {getInputBox()}
      </Box>
      <Tooltip
        trigger={
          <ButtonIcon
            kind='saviGhost'
            ariaLabel='Dictate'
            icon={<Icon kind='Microphone' size='medium' />}
            isDisabled={!onAudio || isDisabled}
            isSelected={isRecording}
            onClick={handleMicClick}
            dataTestId={testIds.GEN_PROMPT_INPUT_MIC_BTN}
          />
        }
        text='Dictate'
        placement='bottom'
      />
      <Tooltip
        trigger={
          <ButtonIcon
            kind='saviGhost'
            isDisabled={(!isProcessing && !isValidValue) || isDisabled}
            ariaLabel={isProcessing ? 'Stop Processing' : 'Submit'}
            icon={
              <Icon
                kind={isProcessing ? 'StopFilled' : 'ArrowUp'}
                size='medium'
              />
            }
            onClick={() => {
              if (isProcessing && onStop) {
                onStop();

                return;
              }

              onSubmit(value.trim());
              setValue('');
            }}
            dataTestId={testIds.GEN_PROMPT_INPUT_SUBMIT_BTN}
          />
        }
        text={isProcessing ? 'Stop Processing' : 'Submit'}
        placement='bottom'
      />
    </Box>
  );
}

PromptInput.defaultProps = {
  isMultiline: false,
  multiInputMaxHeight: 8,
  isDisabled: false,
  isProcessing: false,
  onAddFiles: undefined,
  onStop: undefined,
  onAudio: undefined,
  inputFieldId: 'prompt-input',
  dataTestId: '',
  className: '',
};

export default memo(PromptInput);
